import { useState, useCallback, useEffect } from 'react';
import { managedAjaxUtil, s } from 'Imports';
import { IConfigServiceInjectedProps } from '$State/ConfigFreezerService';
import { IIntegrationPartnerDataInjectedProps } from '$State/IntegrationPartnerDataFreezerService';
import { IAgreementsServiceInjectedProps } from '$State/AgreementsFreezerService';
import { ISinglePageApplicationProps } from '../../spa.types';
import { EncompassManager } from '../../integrationPartner/encompassManager';
import { GeotabManager } from '../../integrationPartner/geotabManager';
import { useIntegrationSetup } from '../../integrationPartner/hooks/useIntegrationSetup';
import { IUserManagementServiceInjectedProps } from '$State/UserManagementFreezerService';
import { init as initApm } from '@elastic/apm-rum';
import favicon = require('../../assets/favicon.ico');

export type AppInitializerHookProps = ISinglePageApplicationProps &
    IConfigServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps &
    IAgreementsServiceInjectedProps &
    IUserManagementServiceInjectedProps;

export const useAppInitializer = (props: AppInitializerHookProps) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isIntegrationConfigured, setIsIntegrationConfigured] = useState(false);

    const { isIntegrationReady, setupIntegrationPartner } = useIntegrationSetup(props);

    useEffect(() => {
        if (isIntegrationReady) {
            if (props.config.isRunningStandAlone()) {
                props.integrationPartnerData.getIntegrationPartnerManager().configureApplicationForStandAloneMode();
            } else if (props.config.isRunningEncompassIntegrated()) {
                props.integrationPartnerData.getIntegrationPartnerManager().configureApplicationForIntegratedMode();
            }
            setIsIntegrationConfigured(true);
        }
    }, [isIntegrationReady]);

    const saveBaseUrl = useCallback(
        (url: string) => {
            props.config.setBaseUrl(url);
            managedAjaxUtil.setBaseUrl(s.rtrim(`${url}`, '/'));
        },
        [props.config],
    );

    const initialize = useCallback(async () => {
        let apm: any;
        try {
            if (location.pathname.includes('/silentRenew')) {
                await EncompassManager.completeCredentialRenewal();
                setIsInitialized(true);
                return;
            }

            props.config.setIsRunningStandAlone(!GeotabManager.isIntegrated() && !EncompassManager.isIntegrated());
            saveBaseUrl(GeotabManager.isIntegrated() ? GeotabManager.getGeotabBaseUrl(props.baseUrl, props.apiBaseUrl) : location.origin);

            const setupConfiguration = async () => {
                await props.config.getClientConfig();
                const showAgreements = props.config.getState().clientConfigResults.data?.showAgreements;
                props.agreements.setShowAgreements(showAgreements);
                props.setFaviconUrl(favicon);
            };

            await setupConfiguration();
            setupIntegrationPartner();

            managedAjaxUtil.setCredentialResolver(async () => {
                const manager = props.integrationPartnerData.getIntegrationPartnerManager();
                return manager ? manager.getCredential() : '';
            });

            const clientConfig = props.config.getState().clientConfigResults.data;
            apm = initApm({
                // Set required service name (allowed characters: a-z, A-Z, 0-9, -, _, and space)
                serviceName: clientConfig?.apmServiceName ?? 'ui_vprotect', //'ui_vprotect',
                // Set custom APM Server URL
                serverUrl: clientConfig?.apmServiceUrl,
                // Set service environment (required for sourcemap feature)
                environment: clientConfig?.deployEnvironment,
            });
            apm.addLabels({ IntegrationPlatform: clientConfig?.integrationPlatform });

            setIsInitialized(true);
        } catch (e) {
            console.error('[useAppInitializer] Initialization error', e);
            setIsInitialized(false);
        }
    }, []);

    const isAppInitialized = isInitialized && isIntegrationReady && isIntegrationConfigured;

    return { isAppInitialized, initialize };
};
